import { useFormik } from "formik";
import React, { useCallback, useState } from "react";
import { Col, Row } from "react-bootstrap";
import * as Yup from "yup";
import SelectFiled from "../../common/textfield/SelectFiled";
import SearchSelect from "../../common/textfield/SearchSelect";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import TextInput from "../../common/textfield/TextInput";
import { Button } from "../../common/Button";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useClinicLocationsQuery } from "../../../hooks/ReactQueryHooks/useClinicLocationsQuery";
import { createKioskDevice } from "../../api/Individual";
import { failed, success } from "../../common/Toastify";
import { validation, conditionalValidation } from "../../../utils/constants"

const CreateDevice = () => {
  const [organizations, setOrganizations] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth?.user);
  const [locations, setLocations] = useState([]);
  const userRoles = useSelector(
    (state) => state?.auth?.user?.["cognito:groups"]
  );
  const admin = userRoles?.includes("Super-Admin");

  const onSuccessLocations = (data) => {
    setLocations(
      data?.map((location) => (location = { ...location, value: location?.id }))
    );
  };

  const onSuccessOrganizations = (data) => {
    setOrganizations(data?.map((org) => ({ ...org, value: org?.id })));
  };
  
  useOrgQuery({
    onSuccess: onSuccessOrganizations,
  });

  const formik = useFormik({
    initialValues: {
      deviceName: "",
      location: "",
      email: "",
      organization:
        user?.["custom:group"] === "Super-Admin"
          ? ""
          : user?.organizations?.[0]?.id,
      type: "kiosk",
    },
    validationSchema: Yup.object({
      deviceName: conditionalValidation(
        Yup.string()
        .max(50, "Must be 50 characters or less")
        .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
        {
          validation,
          mandatory: true,
          label: "Name"
        }),
      location: conditionalValidation(
        Yup.string()
        .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),{
          validation,
          mandatory: true,
          label: "Location"
        }),
      organization: conditionalValidation(
        Yup.string()
        .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
        {
          validation,
          mandatory: true,
          label: "Organization"
        }),
      type: Yup.string()
        .required("Required field")
        .matches(/^\s*\S[\s\S]*$/, "Only blankspaces is not valid."),
      email: conditionalValidation(
        Yup.string()
        .email("Invalid email address"),
        {
          validation,
          mandatory: true,
          label: "Email"
        })
    }),

    onSubmit: (values) => {
      values.deviceName = values.deviceName.trim();
      values.email = values.email.trim();
      setBtnLoading(true);
      let orgData = organizations?.filter(
        (org) => values?.organization === org?.id
      );
      let locationData = locations?.filter(
        (loc) => values?.location === loc?.id
      );
      let locationRegion =
        locationData?.[0]?.address?.[0]?.country +
        "-" +
        locationData?.[0]?.address?.[0]?.state;
      let payload = {
        email: values?.email?.toLowerCase().trim(),
        deviceName: values?.deviceName,
        location: {
          display: locationData?.[0]?.name.trim(),
          reference: `Location/${values?.location}`,
          type: "Location",
        },
        type: values?.type,
        managingOrganization: {
          display: orgData?.[0]?.name.trim(),
          reference: `Organization/${values?.organization}`,
          type: "Organization",
        },
        region: locationData?.[0]?.locationCode || locationRegion || "-",
        orgId: values?.organization,
      };

      createKioskDevice(user?.organizations?.[0]?.id, payload)
        .then((res) => {
          if (res?.status === true) {
            navigate("/app/identities/devices");
            success(res.message);
          } else {
            failed(res.error);
          }
        })
        .catch((res) => {
          failed(
            res?.response?.data?.message ||
              res?.response?.data?.error ||
              res.message
          );
        })
        .finally(() => setBtnLoading(false));
    },
  });

  useClinicLocationsQuery({
    onSuccess: onSuccessLocations,
    orgIds: formik.values.organization? [formik?.values?.organization] : false,
    enabled: Boolean(formik?.values?.organization),
  });
  
  const handleOrgChange = (selected) => {
    const value = selected?.value || "";
    formik.setFieldValue("organization", value);
  };

  const handleLocationChange = (selected) => {
    const value = selected?.value || "";
    formik.setFieldValue("location", value);
  };

  const deviceOptions = [{ name: "Kiosk", value: "kiosk" }];
  const orgSelectOptions = organizations?.map((o) => ({ label: o?.name, value: o?.id ?? o?.value })) || [];
  const locationSelectOptions = locations?.map((l) => ({ label: l?.name, value: l?.id ?? l?.value })) || [];
  const selectedOrg = orgSelectOptions?.find((o) => o?.value === formik?.values?.organization) ?? null;
  const selectedLocation = locationSelectOptions?.find((l) => l?.value === formik?.values?.location) ?? null;

  return (
    <section className="common-listing">
      <div className="custom-card p-4">
        <form className="common-form border-fields userAccount">
          <Row>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"text"}
                keyField={"deviceName"}
                label={"Device Name"}
                formik={formik}
                placeholder={"Device Name"}
                required={true}
              />
            </Col>
            <Col md={3}>
              <SelectFiled
                keyField={"type"}
                label={"Device Type"}
                formik={formik}
                options={deviceOptions}
              />
            </Col>
            <Col md={3}>
              <SearchSelect
                keyField={"organization"}
                label={"Select Organization"}
                formik={formik}
                options={orgSelectOptions}
                value={selectedOrg}
                onChange={handleOrgChange}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextInput
                type={"email"}
                keyField={"email"}
                label={"Email Address"}
                formik={formik}
                placeholder={"Email Address"}
                required={true}
              />
            </Col>
            <Col>
              <SearchSelect
                keyField={"location"}
                label={"Select Location"}
                formik={formik}
                options={locationSelectOptions}
                value={selectedLocation}
                onChange={handleLocationChange}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                style={{ marginRight: "20px" }}
                onClick={() => {
                  navigate("/app/identities/devices");
                }}
                variant="secondary"
                title="Cancel"
              >
                Cancel
              </Button>
              <Button isLoading={btnLoading} onClick={formik.handleSubmit}>
                Create
              </Button>
            </Col>
          </Row>
        </form>
      </div>
    </section>
  );
};

export default CreateDevice;
